import React, { useState, useEffect } from "react";
import { Button, Container } from "reactstrap";
import styles from "../../styles/public/date-selector.module.css";
import Calendar from "../../components/public/Calendar";
import { FaChevronUp, FaChevronDown, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const today = new Date();
today.setHours(0, 0, 0, 0);

const getStartOfWeek = (date) => {
  const start = new Date(date);
  const day = start.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  start.setDate(start.getDate() + diff);
  return start;
};

const isSameWeek = (date1, date2) => getStartOfWeek(date1).getTime() === getStartOfWeek(date2).getTime();

const formatRange = (start, end) => {
  const opts = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
};

const DateSelector = ({
  selectedDate: initialSelectedDate,
  maxDate,
  onDateSelect,
  prices,
  displayPrices = true,
  fetchMorePrices
}) => {

  const generateWeekDays = (startDate) => {
    const days = [];
    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dayName = daysOfWeek[i];
      const dayNum = date.getDate();
      const dateStr = date.toISOString().split("T")[0];
      const priceData = prices?.find((p) => p.date === dateStr);
      const price = priceData?.price ?? null;
      const fullDate = new Date(date.setHours(0, 0, 0, 0));
      const isDisabled = fullDate < today || fullDate >= maxDate;

      days.push({ day: dayName, date: dayNum, price, fullDate, disabled: isDisabled });
    }

    return days;
  };

  const [thisWeekStart, setThisWeekStart] = useState(getStartOfWeek(today));
  const [isThisWeek, setIsThisWeek] = useState(true);
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate ? new Date(initialSelectedDate) : null);
  const [weekLabels, setWeekLabels] = useState({ firstWeek: "This week", secondWeek: "Next week", next: "Next", previous: "Previous" });

  const isSameDay = (d1, d2) =>
    d1 instanceof Date &&
    d2 instanceof Date &&
    !isNaN(d1) &&
    !isNaN(d2) &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const thisWeekDays = generateWeekDays(thisWeekStart);
  const nextWeekStart = new Date(thisWeekStart);
  nextWeekStart.setDate(thisWeekStart.getDate() + 7);
  const nextWeekDays = generateWeekDays(nextWeekStart);
  const currentWeekDays = isThisWeek ? thisWeekDays : nextWeekDays;

  // Update week start and selected date if initialSelectedDate changes
  useEffect(() => {
    if (initialSelectedDate) {
      const newStart = getStartOfWeek(new Date(initialSelectedDate));

      // Only update if different
      if (!isSameWeek(newStart, thisWeekStart)) {
        setThisWeekStart(newStart);
        setIsThisWeek(true);
      }

      setSelectedDate(new Date(initialSelectedDate));
    }
  }, [initialSelectedDate, thisWeekStart]);

  // Update week labels
  useEffect(() => {
    const firstWeekEnd = new Date(thisWeekStart);
    firstWeekEnd.setDate(thisWeekStart.getDate() + 6);
    const secondWeekStart = new Date(thisWeekStart);
    secondWeekStart.setDate(thisWeekStart.getDate() + 7);
    const secondWeekEnd = new Date(thisWeekStart);
    secondWeekEnd.setDate(thisWeekStart.getDate() + 13);

    const thisWeekLabels = { firstWeek: "This week", secondWeek: "Next week", next: "Next", previous: "Previous" };
    const futureWeekLabels = {
      firstWeek: formatRange(thisWeekStart, firstWeekEnd),
      secondWeek: formatRange(secondWeekStart, secondWeekEnd),
      next: "Next",
      previous: "Previous",
    };

    setWeekLabels(isSameWeek(thisWeekStart, today) ? thisWeekLabels : futureWeekLabels);
  }, [thisWeekStart]);

  const handleWeekToggle = () => {
    const newIsThisWeek = !isThisWeek;
    setIsThisWeek(newIsThisWeek);
    setShowFullCalendar(false);
  };

  const handleDateSelect = (date) => {
    if (date >= today) {
      setSelectedDate(date);
      onDateSelect(date);
      setShowFullCalendar(false);
    }
  };

  const handleMonthChange = (newDate) => {
    fetchMorePrices(
      new Date(newDate.getFullYear(), newDate.getMonth(), 1).toISOString().split("T")[0],
      new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).toISOString().split("T")[0]
    );
  }

  const handleSeeCalendar = () => setShowFullCalendar(!showFullCalendar);

  return (
    <Container className={`${styles["date-selector"]} m-auto`}>
      {/* Week header */}
      <div className="week-header mb-2 text-center">
        <div className={`${styles["week-navigation"]} d-flex justify-content-between align-items-center flex-nowrap px-2`}>
          {isThisWeek ? (
            <>
              <div className={styles["week-placeholder"]} />
              <span className={`${styles["week-label"]} fs-3 fw-medium`}>{weekLabels.firstWeek}</span>
              <Button color="link" className={styles["week-nav"]} onClick={handleWeekToggle}>
                {weekLabels.next} <FaChevronRight />
              </Button>
            </>
          ) : (
            <>
              <Button color="link" className={styles["week-nav"]} onClick={handleWeekToggle}>
                <FaChevronLeft /> {weekLabels.previous}
              </Button>
              <span className={`${styles["week-label"]} fs-3 fw-medium`}>{weekLabels.secondWeek}</span>
              <div className={styles["week-placeholder"]} />
            </>
          )}
        </div>
      </div>

      {/* Day buttons */}
      <div className={`${styles["calendar-days"]} d-flex justify-content-center gap-2 mb-3`}>
        {currentWeekDays.map(({ day, date, price, fullDate, disabled }) => (
          <Button
            key={date}
            color="light"
            className={`${styles["day-button"]} ${isSameDay(selectedDate, fullDate) ? styles.selected : ""}`}
            onClick={() => handleDateSelect(fullDate)}
            disabled={disabled}
          >
            <div className={styles.day}>{day}</div>
            <div className={styles.date}>{date}</div>
            {!disabled && displayPrices && <div className={styles["price"]}>{price !== null ? price : prices.length > 0 ? "" : "..."}</div>}
          </Button>
        ))}
      </div>

      {/* Full calendar toggle */}
      <div className="d-flex justify-content-center mb-3">
        <Button color="link" className={styles["see-calendar"]} onClick={handleSeeCalendar}>
          {showFullCalendar ? "Hide full calendar" : "See full calendar"} {showFullCalendar ? <FaChevronUp /> : <FaChevronDown />}
        </Button>
      </div>

      {/* Full calendar component */}
      {showFullCalendar && (
        <div className="mt-3 mb-3">
          <Calendar selectedDate={selectedDate} maxDate={maxDate} onDateSelect={handleDateSelect} prices={prices} onMonthChange={handleMonthChange} />
        </div>
      )}
    </Container>
  );
};

export default DateSelector;
